import { Visual } from 'components/Alpha/cards/cardTypes';
import { IAlphaHandoff, parseHandoff } from 'services/alpha/parseHandoff';
import { toVisual } from 'services/alpha/visuals';

export const INTERRUPTED_REPLY_TEXT = 'This reply was interrupted. Ask again to continue.';

const INTERRUPTED_MARKER = /no reply:?\s*interrupted/i;

export const interruptedReplyText = (synthetic: boolean | undefined, text: string): string | undefined =>
  synthetic && INTERRUPTED_MARKER.test(text) ? INTERRUPTED_REPLY_TEXT : undefined;

export const WRITE_TOOL_NAMES = new Set([
  'add_attachment', 'add_note', 'add_time_entry', 'assign_ticket',
  'create_phase', 'create_project_task', 'create_project', 'create_ticket',
  'set_status', 'update_phase', 'update_project_task', 'update_project',
  'update_ticket', 'add_notification_users', 'create_notification_group',
  'set_oncall_schedule', 'swap_shift',
]);

export interface IStoredToolCall {
  toolName: string;
  label?: string;
}

export type StoredMessageContent = {
  text: string;
  toolCalls?: IStoredToolCall[];
  expiredConfirmationToolName?: string;
  expiredConfirmationTitle?: string;
  visuals?: Visual[];
  handoff?: IAlphaHandoff;
};

const VISUAL_BLOCK = 'alpha_visual';
const HANDOFF_BLOCK = 'alpha_handoff';

const toStoredVisual = (block: unknown): Visual | undefined => {
  if (typeof block !== 'object' || block === null) return undefined;
  const { type, ...visual } = block as Record<string, unknown>;
  return type === VISUAL_BLOCK ? toVisual(visual) : undefined;
};

type AnthropicContentBlock = {
  type?: string;
  text?: string;
  name?: string;
  label?: unknown;
  title?: unknown;
};

const nonBlank = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const storedToolCall = (name: string, label: unknown): IStoredToolCall => {
  const text = nonBlank(label);
  return text ? { toolName: name, label: text } : { toolName: name };
};

export function parseStoredMessage(_role: string, contentJson: string): StoredMessageContent {
  try {
    const parsed = JSON.parse(contentJson);

    if (typeof parsed === 'string') {
      return { text: parsed };
    }

    if (Array.isArray(parsed)) {
      let text = '';
      const toolCalls: IStoredToolCall[] = [];
      const visuals: Visual[] = [];
      let handoff: IAlphaHandoff | undefined;
      const blocks = parsed as AnthropicContentBlock[];

      blocks.forEach(block => {
        if (block?.type === 'text' && typeof block.text === 'string') {
          text += block.text;
        }
        if (block?.type === 'tool_use' && typeof block.name === 'string') {
          toolCalls.push(storedToolCall(block.name, block.label));
        }
        if (block?.type === HANDOFF_BLOCK) {
          handoff = parseHandoff({ handoff: block }) ?? handoff;
        }
        const visual = toStoredVisual(block);
        if (visual) visuals.push(visual);
      });

      const lastBlock = blocks[blocks.length - 1];
      const lastTitle = nonBlank(lastBlock?.title);
      const expiredConfirmationToolName =
        lastBlock?.type === 'tool_use' &&
        typeof lastBlock.name === 'string' &&
        (WRITE_TOOL_NAMES.has(lastBlock.name) || lastTitle !== undefined)
          ? lastBlock.name
          : undefined;

      return {
        text,
        toolCalls: toolCalls.length ? toolCalls : undefined,
        expiredConfirmationToolName,
        ...(visuals.length ? { visuals } : {}),
        ...(handoff ? { handoff } : {}),
        ...(expiredConfirmationToolName && lastTitle
          ? { expiredConfirmationTitle: lastTitle }
          : {}),
      };
    }

    return { text: '' };
  } catch {
    return { text: '' };
  }
}

type StoredRow = { role: string; contentJson: string; synthetic?: boolean };

const isFinishedAssistantRow = (row: StoredRow | undefined): boolean => {
  if (!row || row.role !== 'assistant' || row.synthetic) return false;
  const parsed = parseStoredMessage(row.role, row.contentJson);
  return parsed.text.length > 0 && !parsed.toolCalls;
};

export function hasFinishedReply(stored: StoredRow[], submittedText?: string): boolean {
  if (!isFinishedAssistantRow(stored[stored.length - 1])) return false;
  if (submittedText === undefined) return true;
  for (let i = stored.length - 2; i >= 0; i -= 1) {
    if (stored[i].role === 'user') {
      const text = parseStoredMessage('user', stored[i].contentJson).text.trim();
      if (text.length > 0) return text === submittedText.trim();
    }
  }
  return false;
}
